import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle, Package, ArrowRight, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const CheckoutSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const [status, setStatus] = useState('checking');
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    if (!sessionId) {
      setStatus('error');
      return;
    }

    const pollPaymentStatus = async () => {
      if (attempts >= 5) {
        setStatus('timeout');
        return;
      }

      try {
        const response = await axios.get(
          `${API}/checkout/status/${sessionId}`,
          { withCredentials: true }
        );

        if (response.data.payment_status === 'paid') {
          setStatus('success');
          return;
        } else if (response.data.status === 'expired') {
          setStatus('expired');
          return;
        }

        // Continue polling
        setAttempts(prev => prev + 1);
        setTimeout(pollPaymentStatus, 2000);
      } catch {
        setAttempts(prev => prev + 1);
        setTimeout(pollPaymentStatus, 2000);
      }
    };

    pollPaymentStatus();
  }, [sessionId, attempts]);

  if (status === 'checking') {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="checkout-checking">
        <div className="text-center">
          <Loader2 className="w-16 h-16 text-fuchsia-400 animate-spin mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-white mb-2">Vérification du paiement...</h1>
          <p className="text-gray-400">Veuillez patienter quelques instants</p>
        </div>
      </div>
    );
  }

  if (status === 'success') {
    return (
      <div className="min-h-screen flex items-center justify-center px-4" data-testid="checkout-success">
        <div className="text-center max-w-lg">
          <div className="w-20 h-20 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-green-400" />
          </div>
          
          <h1 className="text-3xl font-bold text-white mb-4 font-outfit">
            Commande confirmée !
          </h1>
          
          <p className="text-gray-400 mb-8">
            Merci pour votre achat. Vous recevrez un email de confirmation avec les détails de votre commande.
          </p>

          <div className="bg-[#121212] rounded-xl p-6 border border-white/10 mb-8">
            <div className="flex items-center justify-center gap-4 mb-4">
              <Package className="w-8 h-8 text-fuchsia-400" />
              <div>
                <p className="text-white font-semibold">Livraison estimée</p>
                <p className="text-gray-400 text-sm">3-5 jours ouvrés</p>
              </div>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/orders">
              <Button className="w-full sm:w-auto bg-fuchsia-600 hover:bg-fuchsia-500 text-white" data-testid="view-orders-btn">
                Voir mes commandes
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
            <Link to="/shop">
              <Button variant="outline" className="w-full sm:w-auto border-white/20 text-white" data-testid="continue-shopping-btn">
                Continuer mes achats
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Error/timeout/expired states
  return (
    <div className="min-h-screen flex items-center justify-center px-4" data-testid="checkout-error">
      <div className="text-center max-w-lg">
        <div className="w-20 h-20 rounded-full bg-yellow-500/20 flex items-center justify-center mx-auto mb-6">
          <Package className="w-10 h-10 text-yellow-400" />
        </div>
        
        <h1 className="text-3xl font-bold text-white mb-4 font-outfit">
          {status === 'expired' ? 'Session expirée' : 'Vérification en cours'}
        </h1>
        
        <p className="text-gray-400 mb-8">
          {status === 'expired' 
            ? 'Votre session de paiement a expiré. Veuillez réessayer.'
            : 'Nous vérifions votre paiement. Si vous avez déjà payé, consultez votre email pour confirmation.'}
        </p>

        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Link to="/orders">
            <Button className="w-full sm:w-auto bg-fuchsia-600 hover:bg-fuchsia-500 text-white">
              Voir mes commandes
            </Button>
          </Link>
          <Link to="/shop">
            <Button variant="outline" className="w-full sm:w-auto border-white/20 text-white">
              Retour à la boutique
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
};
